import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Navigation,
  Truck,
  Wrench,
  Tag,
  Camera,
  Images,
} from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import {
  CONTACT,
  CONTACT_INTRO,
  HOURS,
  PICKUP_NOTE,
  LANGUAGES,
  LINKS,
  CTA_CARDS,
  SERVICE_AREAS,
  SERVICE_AREA_INTRO,
} from "../data/site";

const ctaIcons = { tag: Tag, camera: Camera, images: Images };

export const ContactMap = () => (
  <section id="kapcsolat" data-testid="contact-section" className="scroll-mt-24 py-24 sm:py-28">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHeading eyebrow={CONTACT_INTRO.eyebrow} title={CONTACT_INTRO.title} />

      <div className="mt-12 grid gap-6 lg:grid-cols-2">
        <Reveal>
          <div
            data-testid="contact-info-card"
            className="h-full rounded-3xl border border-line bg-white p-8 card-shadow"
          >
            <div className="space-y-7">
              <div className="flex gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-burgundy/10 text-burgundy">
                  <MapPin className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="font-display text-xl font-semibold text-ink">Címünk</h3>
                  <p className="mt-1 text-sm font-semibold text-ink sm:text-base" data-testid="contact-address">
                    {CONTACT.address}
                  </p>
                  <p
                    className="mt-2.5 inline-flex items-center gap-2 rounded-full bg-cream2 px-4 py-2 text-xs font-bold text-ink"
                    data-testid="contact-address-note"
                  >
                    <Navigation className="h-3.5 w-3.5 text-burgundy" />
                    {CONTACT.addressNote}
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-burgundy/10 text-burgundy">
                  <Phone className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="font-display text-xl font-semibold text-ink">Elérhetőség</h3>
                  <a
                    href={CONTACT.phoneHref}
                    data-testid="contact-phone-link"
                    className="mt-1 block text-sm font-bold text-burgundy sm:text-base"
                  >
                    {CONTACT.phone}
                  </a>
                  <a
                    href={CONTACT.centralPhoneHref}
                    data-testid="contact-central-phone-link"
                    className="mt-1 block text-sm font-semibold text-ink hover:text-burgundy"
                  >
                    Központi: {CONTACT.centralPhone}
                  </a>
                  <a
                    href={`mailto:${CONTACT.email}`}
                    data-testid="contact-email-link"
                    className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-ink hover:text-burgundy"
                  >
                    <Mail className="h-3.5 w-3.5" /> {CONTACT.email}
                  </a>
                  <div className="mt-3 flex flex-wrap gap-2.5" data-testid="contact-languages">
                    {LANGUAGES.map((l) => (
                      <span
                        key={l.label}
                        className="flex items-center gap-2 rounded-full bg-cream2 px-3.5 py-1.5 text-xs font-bold text-ink"
                      >
                        <img src={l.flag} alt={l.alt} className="h-3.5 w-5 rounded-sm object-cover" />
                        {l.label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-burgundy/10 text-burgundy">
                  <Clock className="h-5 w-5" />
                </span>
                <div className="flex-1">
                  <h3 className="font-display text-xl font-semibold text-ink">Nyitvatartás</h3>
                  <div className="mt-2 space-y-1.5" data-testid="opening-hours-list">
                    {HOURS.map((h) => (
                      <div key={h.days} className="flex justify-between gap-6 text-sm sm:text-base">
                        <span className="text-soft">{h.days}</span>
                        <span className="font-bold text-ink">{h.time}</span>
                      </div>
                    ))}
                  </div>
                  <p
                    className="mt-4 rounded-2xl bg-cream2 p-4 text-xs leading-relaxed text-soft sm:text-sm"
                    data-testid="pickup-note"
                  >
                    {PICKUP_NOTE}
                  </p>
                </div>
              </div>
            </div>

            <a
              href={LINKS.mapsDirections}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="directions-button"
              className="btn-pill mt-8 w-full justify-center bg-burgundy py-4 text-cream hover:scale-[1.02]"
            >
              <Navigation className="h-4 w-4" /> Útvonaltervezés
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div
            data-testid="map-frame"
            className="overflow-hidden rounded-3xl border border-line card-shadow"
          >
            <iframe
              title="Comfort Line Bútoráruház Keszthely térkép"
              data-testid="google-map-embed"
              src={LINKS.mapsEmbed}
              className="h-[380px] w-full lg:h-full lg:min-h-[560px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </Reveal>
      </div>

      <div className="mt-6 grid gap-5 md:grid-cols-3" data-testid="contact-cta-cards">
        {CTA_CARDS.map((c, i) => {
          const Icon = ctaIcons[c.icon];
          return (
            <Reveal key={c.title} delay={i * 0.08}>
              <a
                href={c.href}
                target="_blank"
                rel="nofollow noopener noreferrer"
                data-testid={c.testid}
                className="group flex items-center gap-4 rounded-3xl border border-line bg-white p-6 card-shadow transition-transform duration-300 hover:-translate-y-1"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-burgundy/10 text-burgundy transition-colors group-hover:bg-burgundy group-hover:text-cream">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="font-display text-lg font-semibold text-ink">{c.title}</h3>
              </a>
            </Reveal>
          );
        })}
      </div>

      <Reveal delay={0.1}>
        <div
          data-testid="service-area-block"
          className="mt-6 rounded-3xl bg-night p-8 text-cream sm:p-10"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-burgundy text-cream">
              <Truck className="h-5 w-5" />
            </span>
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-cream">
              <Wrench className="h-5 w-5" />
            </span>
            <h3 className="ml-2 font-display text-xl font-semibold sm:text-2xl">
              {SERVICE_AREA_INTRO.title}
            </h3>
          </div>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-cream/70 sm:text-base">
            {SERVICE_AREA_INTRO.text}
          </p>
          <div className="mt-6 flex flex-wrap gap-2.5" data-testid="service-area-chips">
            {SERVICE_AREAS.map((town, i) => (
              <span
                key={town}
                data-testid={`service-area-${i}`}
                className="rounded-full border border-cream/20 px-4 py-1.5 text-xs font-bold text-cream"
              >
                {town}
              </span>
            ))}
            <span className="rounded-full border border-dashed border-gold/60 px-4 py-1.5 text-xs font-bold text-gold">
              + vonzáskörzetük
            </span>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
